// src/pages/Reports/CurrentLiability/CurrentLiabilityLedgerReport.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  DatePicker,
  AutoComplete,
  Input,
  Table,
  message,
  Grid,
} from "antd";
import {
  ArrowLeftOutlined,
  PrinterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import commaNumber from "comma-number";
import _ from "lodash";

import ReportDom from "../../../components/common/ReportDom";
import RecordDateFilter from "../../../components/filters/RecordDateFilter";
import { useDefaultDateRange } from "../../../hooks/useDefaultDateRange";
import {
  currentDateStartTime,
  currentDateEndTime,
  dateFormat,
} from "../../../lib/functions";

import {
  getCurrentLiabilityLedgerApi,
} from "../../../apiservice/reports";
import {
  getAccountBySeachApi,
} from "../../../apiservice/setting";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";
import "../../../components/common/bt.css";

const { Title } = Typography;
const format = commaNumber.bindWith(",", ".");

const fmt = (num) =>
  Number(num || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const CurrentLiabilityLedgerReport = () => {
  const navigate = useNavigate();
  const reportRef = useRef();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  // initial filters use start/end of today
  const [filters, setFilters] = useState({
    account: null,
    fromDate: currentDateStartTime(dayjs()),
    toDate: currentDateEndTime(dayjs()),
  });

  // apply the same hook used elsewhere to default dates on first load
  useDefaultDateRange(
    // mimic the signature used by other pages: this reducer setter would normally be passed,
    // but here we'll keep local state so we use a small wrapper function to update state.
    // useDefaultDateRange expects (setFiltersFn, filtersObj, defaultRange)
    // we'll provide setFiltersWrapper to update local state
    (payload) => setFilters((prev) => ({ ...prev, ...payload })),
    filters,
    "today"
  );

  const [data, setData] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accLoading, setAccLoading] = useState(false);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };


  // fetch accounts (autocomplete source)
  const fetchAccounts = async (query) => {
    setAccLoading(true);
    try {
      const res = await getAccountBySeachApi({
        query: query?.trim() || "",
        multiType: ["current_liability"],
      });
      setAccounts(res.data || []);
    } catch (err) {
      console.error("Error fetching accounts:", err);
    } finally {
      setAccLoading(false);
    }
  };

  useEffect(() => {
    // initial load for account dropdown
    fetchAccounts("");
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const fetchReport = async () => {
    if (!filters.account) {
      message.warning("Please select an account");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        accId: filters.account?.acc_id,
        dateFrom: filters.fromDate,
        dateTo: filters.toDate,
      };

      const res = await getCurrentLiabilityLedgerApi(payload);

      // API returns { opening_balance, ledger, closing_balance } similar to old UI
      setOpeningBalance(res.data.opening_balance || 0);
      setData(res.data.ledger || []);
      setClosingBalance(res.data.closing_balance || 0);
    } catch (err) {
      console.error(err);
      message.error("Error fetching Current Liability Ledger");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Current Liability Ledger",
    removeAfterPrint: true,
  });

  const handleNavigateToBack = () => {
    navigate("/reports");
  };

  // compute totals
  const debitTotal = _.sumBy(data, (r) => Number(r.debit_amount || 0));
  const creditTotal = _.sumBy(data, (r) => Number(r.credit_amount || 0));

  // We'll build tableData including opening, ledger rows, totals & closing to control colSpan
  const ledgerWithSeq = (data || []).map((r, i) => ({ ...r, seq: i + 1 }));

  const tableData = [
    { id: "opening", rowType: "opening", balance: openingBalance },
    ...ledgerWithSeq.map((row, idx) => ({
      ...row,
      id: row.id || `ledger-${idx}`,
      rowType: "ledger",
    })),
    { id: "totals", rowType: "totals", debit_total: debitTotal, credit_total: creditTotal },
    { id: "closing", rowType: "closing", balance: closingBalance },
  ];

  const columns = [
    {
      title: "No.",
      key: "no",
      width: 60,
      render: (_, record) =>
        record.rowType === "ledger" ? record.seq : record.rowType === "totals" ? "" : null,
    },
    {
      title: "Date",
      dataIndex: "created_date",
      key: "date",
      render: (val, record) => {
        // For opening/totals/closing we return an object so we can control colSpan
        if (record.rowType === "opening")
          return {
            children: "Opening Balance :",
            props: { colSpan: 4, style: { textAlign: "right", fontWeight: 700 } },
          };
        if (record.rowType === "totals")
          return {
            children: "Grand Total :",
            props: { colSpan: 4, style: { textAlign: "right", fontWeight: 700 } },
          };
        if (record.rowType === "closing")
          return {
            children: "Closing Balance :",
            props: { colSpan: 4, style: { textAlign: "right", fontWeight: 700 } },
          };
        // normal ledger row
        return val ? dayjs(val).format("DD-MMM-YYYY") : "";
      },
    },
    {
      title: "Particular",
      dataIndex: "particular",
      key: "particular",
      render: (val, record) =>
        record.rowType !== "ledger" ? { children: null, props: { colSpan: 0 } } : val,
    },
    {
      title: "VCH No",
      dataIndex: "vch_no",
      key: "vch_no",
      render: (val, record) =>
        record.rowType !== "ledger" ? { children: null, props: { colSpan: 0 } } : val,
    },
    {
      title: "VCH Type",
      dataIndex: "vch_type",
      key: "vch_type",
      render: (val, record) =>
        record.rowType !== "ledger" ? { children: null, props: { colSpan: 0 } } : val,
    },
    {
      title: "Debit",
      dataIndex: "debit_amount",
      key: "debit",
      align: "right",
      render: (val, record) => {
        if (record.rowType === "opening") {
          // opening: show openingBalance under Debit (old UI)
          return <strong>{fmt(record.balance)}</strong>;
        }
        if (record.rowType === "closing") {
          // closing: leave Debit blank (old UI)
          return "";
        }
        if (record.rowType === "totals") {
          return <strong>{fmt(record.debit_total)}</strong>;
        }
        return fmt(record.debit_amount);
      },
    },
    {
      title: "Credit",
      dataIndex: "credit_amount",
      key: "credit",
      align: "right",
      render: (val, record) => {
        if (record.rowType === "opening") {
          // opening: leave Credit blank
          return "";
        }
        if (record.rowType === "closing") {
          // closing: show closingBalance under Credit (old UI)
          return <strong>{fmt(record.balance)}</strong>;
        }
        if (record.rowType === "totals") {
          return <strong>{fmt(record.credit_total)}</strong>;
        }
        return fmt(record.credit_amount);
      },
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      align: "right",
      render: (val, record) =>
        record.rowType === "ledger" ? fmt(val) : "",
    },
  ];

  return (
    <div className="p-4">
      {/* Header */}
      <Card
        className="shadow-md rounded-xl mb-4"
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleNavigateToBack}
              style={{ marginRight: 8 }}
            />
            <Title level={4} style={{ margin: 0 }} className="reportBigTitle" >
              Current Liability Ledger
            </Title>
          </div>
        }
        extra={
          <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
            { !screens.xs && "Print" }
          </Button>
        }
      />

      {/* Filters */}
      <Card className="shadow-md rounded-xl mb-4">
        <Row gutter={[16, 16]}>
          {/* Account Selector */}
          <Col xs={24} sm={12} md={8} lg={8}>
            <label>Select Account</label>
            <AutoComplete
              style={{ width: "100%" }}
              options={accounts.map((a) => ({
                label: `${a.acc_name} - ${a.acc_code || ""}`,
                value: `${a.acc_name} - ${a.acc_code || ""}`,
                account: a,
              }))}
              onSelect={(_, option) => {
                handleFilterChange("account", option.account);
              }}
              onSearch={fetchAccounts}
              placeholder="Search Account"
              loading={accLoading}
              allowClear
            >
              <Input />
            </AutoComplete>
          </Col>

          {/* Date controls (use RecordDateFilter for quick ranges) */}
          {/* <RecordDateFilter filters={filters} handleFilterChange={handleFilterChange} /> */}
          <CommonDateRangeFilter
            filters={filters}
            onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
            }}
          />

          {/* Report Button */}
          <Col xs={24} sm={12} md={6} lg={4} style={{ display: "flex",alignItems: "flex-end", height: 58 }}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={fetchReport}
              loading={loading}
              style={{height:35.19}}
              block
            >
              Report
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Report Preview */}
      <Card className="shadow-md rounded-xl">
        <Table
          dataSource={tableData}
          rowKey={(row) => row.id}
          columns={columns}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
          }}
          onChange={handleTableChange}
          bordered
          size="small"
          scroll={{ x: "max-content" }}
          summary={() => null
            // show summary only if there's data or opening/closing present
            // (data.length > 0 || openingBalance || closingBalance) && (
            //   <>
            //     {/* We'll use Grand Total row in summary for parity with old UI.
            //         Note: for clarity we keep totals computed from raw data fields */}
            //     <Table.Summary.Row>
            //       <Table.Summary.Cell colSpan={5} align="right">
            //         Grand Total :
            //       </Table.Summary.Cell>
            //       <Table.Summary.Cell align="right">
            //         <strong>{fmt(debitTotal)}</strong>
            //       </Table.Summary.Cell>
            //       <Table.Summary.Cell align="right">
            //         <strong>{fmt(creditTotal)}</strong>
            //       </Table.Summary.Cell>
            //       <Table.Summary.Cell />
            //     </Table.Summary.Row>
            //   </>
            // )
          }
        />
      </Card>

      {/* Print Layout */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Current Liability Ledger"
          mode="report-page"
          filters={filters}
          renderTable={() => (
            <table className="ledger-print-table">
              <colgroup>
                <col style={{ width: "4%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "29%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
              </colgroup>
              <tbody>
                <tr className="ledger-print-heading">
                  <td>No.</td>
                  <td>Date</td>
                  <td>Particular</td>
                  <td>VCH No</td>
                  <td>VCH Type</td>
                  <td style={{ textAlign: "right" }}>Debit</td>
                  <td style={{ textAlign: "right" }}>Credit</td>
                  <td style={{ textAlign: "right" }}>Balance</td>
                </tr>

                <tr className="ledger-opening-row">
                  <td colSpan={5} style={{ textAlign: "right" }}>
                    Opening Balance
                  </td>
                  <td style={{ textAlign: "right" }}>{fmt(openingBalance)}</td>
                  <td />
                  <td />
                </tr>

                {ledgerWithSeq.map((row, index) => (
                  <tr key={row.id || index}>
                    <td>{row.seq}</td>
                    <td>{row.created_date ? dayjs(row.created_date).format("DD-MMM-YYYY") : "-"}</td>
                    <td>{row.particular}</td>
                    <td>{row.vch_no}</td>
                    <td>{row.vch_type}</td>
                    <td style={{ textAlign: "right" }}>{fmt(row.debit_amount)}</td>
                    <td style={{ textAlign: "right" }}>{fmt(row.credit_amount)}</td>
                    <td style={{ textAlign: "right" }}>{fmt(row.balance)}</td>
                  </tr>
                ))}

                <tr className="ledger-total-row">
                  <td colSpan={5} style={{ textAlign: "right" }}>Grand Total</td>
                  <td style={{ textAlign: "right" }}>{fmt(debitTotal)}</td>
                  <td style={{ textAlign: "right" }}>{fmt(creditTotal)}</td>
                  <td />
                </tr>

                <tr className="ledger-closing-row">
                  <td colSpan={6} style={{ textAlign: "right" }}>
                    Closing Balance
                  </td>
                  <td style={{ textAlign: "right" }}>{fmt(closingBalance)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          )}
        />
      </div>
    </div>
  );
};

export default CurrentLiabilityLedgerReport;
